import React from "react";
import { Input } from "../../ui/input";
import { MapPin, Navigation, ExternalLink } from "lucide-react";
import StateProvinceSelect from "../../StateProvinceSelect";
import { formatPostal } from "../../../utils/addressUtils";

const getFullAddress = (formData) => {
  const parts = [
    formData.customer_address,
    formData.address_city,
    formData.address_state,
    formData.zip_code,
  ].filter(Boolean);
  return parts.join(", ");
};

const MapsButtons = ({ formData }) => {
  const fullAddr = getFullAddress(formData);
  if (!fullAddr) return null;
  const encoded = encodeURIComponent(fullAddr);
  return (
    <div className="flex items-center gap-2 mt-1">
      <a
        href={`https://www.google.com/maps/search/?api=1&query=${encoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors"
        data-testid="google-maps-btn"
      >
        <Navigation className="w-3 h-3" />
        Google Maps
        <ExternalLink className="w-2.5 h-2.5" />
      </a>
      <a
        href={`https://maps.apple.com/?q=${encoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-gray-500/10 text-gray-600 dark:text-gray-400 hover:bg-gray-500/20 transition-colors"
        data-testid="apple-maps-btn"
      >
        <Navigation className="w-3 h-3" />
        Apple Maps
        <ExternalLink className="w-2.5 h-2.5" />
      </a>
    </div>
  );
};

/**
 * AddressSection — service-address fields on the Job form.
 *
 * Just renders the four address inputs (street with Google Places autocomplete
 * + city / state / zip). The Address Picker modal that pops up when an
 * existing customer is selected is implemented separately in
 * `<CustomerAddressPicker>` and mounted by `JobFormDialog` /
 * `StoreJobFormDialog` directly — this component is purely the form fields,
 * not the picker, so both single-location and multi-location flows can
 * share the same picker UX.
 */
const AddressSection = ({
  formData, setFormData,
  addressSuggestions, showAddressSuggestions, addressLoading,
  handleAddressChange, selectAddress, setShowAddressSuggestions,
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[#FFCC00]">
        <MapPin className="w-4 h-4" />
        <span className="text-sm font-medium">Service Address</span>
      </div>

      {/* Street Address with Autocomplete */}
      <div className="relative">
        <Input
          value={formData.customer_address || ""}
          onChange={(e) => handleAddressChange(e.target.value)}
          onFocus={() => (formData.customer_address || "").length > 3 && addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
          onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
          className="input-glass"
          placeholder="Street Address"
          data-testid="address-input"
        />
        {addressLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-[#FFCC00]/30 border-t-[#FFCC00] rounded-full animate-spin" />
          </div>
        )}
        {showAddressSuggestions && addressSuggestions.length > 0 && (
          <div
            className="absolute z-[9999] w-full mt-1 bg-popover border border-border rounded-lg shadow-xl overflow-hidden max-h-[200px] overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {addressSuggestions.map((suggestion, i) => (
              <button
                key={suggestion.place_id || i}
                type="button"
                onClick={() => selectAddress(suggestion)}
                className="w-full px-4 py-2.5 text-left text-muted-foreground hover:bg-theme-hover flex items-center gap-2 border-b border-theme last:border-0"
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin className="w-4 h-4 text-[#FFCC00]/60 flex-shrink-0" />
                <span className="truncate">{suggestion.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* City, State, Zip — row layout */}
      <div className="grid grid-cols-5 gap-2">
        <div className="col-span-2">
          <Input
            value={formData.address_city || ""}
            onChange={(e) => setFormData(prev => ({ ...prev, address_city: e.target.value }))}
            className="input-glass"
            placeholder="City"
            data-testid="address-city-input"
          />
        </div>
        <div className="col-span-1">
          <StateProvinceSelect
            value={formData.address_state || ""}
            onChange={(v) => setFormData(prev => ({ ...prev, address_state: v }))}
            className="input-glass"
            data-testid="address-state-input"
          />
        </div>
        <div className="col-span-2">
          <Input
            value={formData.zip_code || ""}
            onChange={(e) => setFormData(prev => ({ ...prev, zip_code: formatPostal(e.target.value) }))}
            className="input-glass"
            placeholder="Zip / Postal"
            maxLength={10}
            data-testid="address-zip-input"
          />
        </div>
      </div>

      <MapsButtons formData={formData} />
    </div>
  );
};

export default AddressSection;
